import { DateTime } from "luxon";

export const CHECKLIST_SECTION_NAME = "Checklist";
export const CHECKLIST_FORM_ID = 300;
export const CHECKLIST_HONEYPOT_QUESTION_IDS = ["19r", "19c"] as const;

type SectionCompleteRealtimeEvent = {
  questionId: string;
  response: string;
};

function honeypotValue(values: Record<string, unknown> | null | undefined, questionId: string): unknown {
  if (!values) return undefined;
  if (Object.prototype.hasOwnProperty.call(values, questionId)) {
    return values[questionId];
  }
  const suffix = questionId.slice(questionId.search(/[a-zA-Z]/));
  const postedKey = `${CHECKLIST_FORM_ID + 19}${suffix}`;
  if (Object.prototype.hasOwnProperty.call(values, postedKey)) {
    return values[postedKey];
  }
  return undefined;
}

function isFilledHoneypotValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "number") return value >= 1;
  const text = String(value).trim();
  if (!text) return false;
  if (text === "0") return false;
  return true;
}

export function realtimeEventsAfterSectionComplete(
  sectionName: string | null | undefined,
  formId: number | null | undefined,
  values: Record<string, unknown> | null | undefined,
  oopsCount: number,
): SectionCompleteRealtimeEvent[] {
  const events: SectionCompleteRealtimeEvent[] = [];
  const isChecklist =
    String(sectionName || "") === CHECKLIST_SECTION_NAME && Number(formId) === CHECKLIST_FORM_ID;

  if (isChecklist) {
    for (const questionId of CHECKLIST_HONEYPOT_QUESTION_IDS) {
      if (!isFilledHoneypotValue(honeypotValue(values, questionId))) {
        events.push({ questionId, response: "" });
      }
    }
  }

  events.push({
    questionId: "NextSection",
    response: String(oopsCount ?? 0),
  });
  return events;
}

function resolveFormId(
  testState: any,
  currentSection: any,
  postSectionData: Record<string, unknown> | null
): number {
  const fromCurrent = Number(currentSection?.formId || 0);
  if (fromCurrent > 0) return fromCurrent;

  const fromPost = Number(postSectionData?.FormId || 0);
  if (fromPost > 0) return fromPost;

  const sectionId = Number(currentSection?.id ?? postSectionData?.SectionId ?? 0);
  if (sectionId <= 0 || !Array.isArray(testState?.testTypes)) return 0;

  for (const testType of testState.testTypes) {
    const section = (testType?.sections || []).find(
      (s: any) => Number(s?.id || s?.sectionId || 0) === sectionId
    );
    const formId = Number(section?.formId || 0);
    if (formId > 0) return formId;
  }

  return 0;
}

export function buildNextSectionRealtimePostData(
  testState: any,
  currentSection: any,
  oopsCount: number,
  postSectionData: Record<string, unknown> | null = null
) {
  const clientTime = DateTime.now().toISO();
  return {
    questionId: "NextSection",
    response: String(oopsCount ?? 0),
    Name: currentSection?.name || postSectionData?.Name || "Request",
    FormId: resolveFormId(testState, currentSection, postSectionData),
    SectionId: currentSection?.id ?? postSectionData?.SectionId,
    Code: testState?.testData?.code ?? postSectionData?.Code ?? "demo",
    LanguageId: testState?.languageId ?? testState?.testData?.languageId,
    TestId: testState?.testData?.id ?? postSectionData?.TestId ?? 0,
    CompanyId: testState?.testData?.companyId ?? 0,
    ClientTime: clientTime,
    When: clientTime,
  };
}

export function isSectionPersistenceVerified(resp: { jsonData?: { sectionPersistenceVerified?: boolean } } | null | undefined) {
  // PostSection sets this from Responses persistence only; RealtimeResponses are not gated here.
  return resp?.jsonData?.sectionPersistenceVerified !== false;
}
